import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Review {
  id: string;
  productId: number;
  userId: string;
  userName: string;
  rating: number; // 1-5
  title: string;
  comment: string;
  createdAt: string;
  verified: boolean;
}

interface ReviewState {
  reviews: Review[];
  
  addReview: (review: Omit<Review, 'id' | 'createdAt'>) => void;
  getReviewsByProduct: (productId: number) => Review[];
  getProductRating: (productId: number) => { average: number; count: number };
  hasUserReviewed: (productId: number, userId: string) => boolean;
  deleteReview: (reviewId: string) => void;
}

export const useReviewStore = create<ReviewState>()(
  persist(
    (set, get) => ({
      reviews: [],

      addReview: (review) => {
        const newReview: Review = {
          ...review,
          id: `REV-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
          createdAt: new Date().toISOString(),
        };
        set((state) => ({
          reviews: [newReview, ...state.reviews],
        }));
      },

      getReviewsByProduct: (productId) => {
        return get().reviews
          .filter((r) => r.productId === productId)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      },

      getProductRating: (productId) => {
        const productReviews = get().reviews.filter((r) => r.productId === productId);
        if (productReviews.length === 0) return { average: 0, count: 0 };
        const sum = productReviews.reduce((acc, r) => acc + r.rating, 0);
        return {
          average: Math.round((sum / productReviews.length) * 10) / 10,
          count: productReviews.length,
        };
      },

      hasUserReviewed: (productId, userId) => {
        return get().reviews.some((r) => r.productId === productId && r.userId === userId);
      },

      deleteReview: (reviewId) => {
        set((state) => ({
          reviews: state.reviews.filter((r) => r.id !== reviewId),
        }));
      },
    }),
    {
      name: 'trax-reviews',
    }
  )
);
